import * as React from "react";
import {useMemo} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import {ColumnDef} from "@tanstack/react-table";
import {useTranslation} from "react-i18next";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {LogsService} from "crqlar-react-common/dist/services/logs/logs.service";
import {
    DataType,
    DataTypeOperator,
    FilterRule
} from "crqlar-react-common/dist/models/audience/filter.model";
import {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {
    ActionType,
    AuditAction,
    AuditOutcome,
    AuditSource,
    UserLogs
} from "crqlar-react-common/dist/models/logs/logs.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useAllResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {ElementChip} from "../../common/uiElements/base/ElementChip";

const AUDIT_ACTIONS = Object.values(AuditAction);

const AUDIT_SOURCES = Object.values(AuditSource);

const AUDIT_OUTCOMES = Object.values(AuditOutcome);

const textFilterValues = (rules: FilterRule[], field: string): string[] | undefined => {
    const values = rules
        .filter(rule => rule.field === field)
        .flatMap(rule => rule.textValues ?? [])
        .filter((value): value is string => !!value);

    const uniqueValues = Array.from(new Set(values));
    return uniqueValues.length ? uniqueValues : undefined;
};

const enumFilterValues = <T extends string>(
    rules: FilterRule[],
    field: string,
    allowedValues: readonly T[]
): T[] | undefined => {
    const allowed = new Set<string>(allowedValues);
    const values = textFilterValues(rules, field)?.filter((value): value is T => allowed.has(value));
    return values?.length ? values : undefined;
};

const firstTextFilterValue = (rules: FilterRule[], field: string): string | undefined =>
    textFilterValues(rules, field)?.[0];

const localDateBoundaryIso = (value: Date | undefined, nextDay = false): string | undefined => {
    if (!value || isNaN(value.getTime())) return undefined;

    // FilterPanel parses YYYY-MM-DD at UTC midnight. Rebuild that calendar date locally
    // so the API receives the same day the user selected in every timezone.
    const boundary = new Date(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate() + (nextDay ? 1 : 0)
    );
    return boundary.toISOString();
};

const auditDateRange = (rules: FilterRule[]) => {
    const starts: string[] = [];
    const ends: string[] = [];

    rules.filter(rule => rule.field === "occurredAt").forEach(rule => {
        const [first, second] = rule.timestampValues ?? [];

        if (rule.operator === DataTypeOperator.BETWEEN) {
            const start = localDateBoundaryIso(first);
            const end = localDateBoundaryIso(second, true);
            if (start) starts.push(start);
            if (end) ends.push(end);
        } else if (rule.operator === DataTypeOperator.MORE_THAN_EQUAL) {
            const start = localDateBoundaryIso(first);
            if (start) starts.push(start);
        } else if (rule.operator === DataTypeOperator.LESS_THAN_EQUAL) {
            const end = localDateBoundaryIso(first, true);
            if (end) ends.push(end);
        }
    });

    const sortedStarts = starts.sort();
    return {
        startDate: sortedStarts.length ? sortedStarts[sortedStarts.length - 1] : undefined,
        endDate: ends.length ? ends.sort()[0] : undefined
    };
};

/**
 * Turns a camelCase / snake_case field key into a human readable label.
 * e.g. "firstName" -> "First name", "arrival_date" -> "Arrival date"
 */
const humanizeKey = (key: string): string => {
    const spaced = key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const isIsoDate = (value: string): boolean =>
    /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}.*)?$/.test(value);

interface LogChange {
    label: string;
    oldValue: string;
    newValue: string;
    changed: boolean;
    redacted?: boolean;
}

const effectiveAction = (item: UserLogs): string | null | undefined => item.action || item.actionType;

const logTimestamp = (item: UserLogs): Date | undefined => {
    if (item.occurredAt instanceof Date) return item.occurredAt;
    if (item.occurredAt) {
        const parsed = new Date(item.occurredAt);
        if (!isNaN(parsed.getTime())) return parsed;
    }
    return item.insertDate;
};

const targetLabel = (item: UserLogs): string =>
    item.target?.displayName
    || item.target?.id
    || item.entityId
    || (item.target?.type ? humanizeKey(item.target.type) : undefined)
    || (item.entityName ? humanizeKey(item.entityName) : undefined)
    || "-";

const enumLabel = (value: string | null | undefined): string =>
    value ? humanizeKey(value) : "-";

const isResourceKey = (key: string): boolean => {
    const normalized = key.toLowerCase();
    return normalized.includes("resource") && normalized.includes("id");
};

const safeParse = (json: string | null | undefined): Record<string, any> => {
    if (!json) return {};
    try {
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const LogsTable = () => {
    const {t} = useTranslation();
    const theme = useTheme();
    const sectors = useAllSectors();
    const resources = useAllResources();

    const resourceNameById = useMemo(() => {
        const map = new Map<string, string>();
        resources.forEach(r => {
            if (r.id && r.name) map.set(r.id, r.name);
        });
        return map;
    }, [resources]);

    const sectorName = React.useCallback((id: string | null | undefined): string => {
        if (!id) return "-";
        const sector = sectors.find(s => s.id === id);
        if (!sector) return "-";
        return sector.name || (sector.sectorType ? t(sector.sectorType) : "-");
    }, [sectors, t]);

    /** Resolves a single resource id to its name, falling back to the id when unknown. */
    const resourceName = React.useCallback(
        (id: string): string => resourceNameById.get(id) || id,
        [resourceNameById]
    );

    /** Formats a resource id field by showing resource names instead of raw ids. */
    const formatResourceValue = React.useCallback((value: any): string => {
        if (value === null || value === undefined || value === "") {
            return t("empty");
        }
        if (Array.isArray(value)) {
            if (value.length === 0) return t("empty");
            return value
                .filter(v => v !== null && v !== undefined && v !== "")
                .map(v => resourceName(String(v)))
                .join(", ");
        }
        return resourceName(String(value));
    }, [resourceName, t]);

    const formatValue = React.useCallback((value: any): string => {
        const format = (currentValue: any): string => {
            if (currentValue === null || currentValue === undefined || currentValue === "") {
                return t("empty");
            }
            if (typeof currentValue === "boolean") {
                return currentValue ? t("Yes") : t("No");
            }
            if (typeof currentValue === "number") {
                return String(currentValue);
            }
            if (typeof currentValue === "string") {
                const normalized = currentValue.trim();
                if (!normalized) return t("empty");
                if (isIsoDate(normalized)) {
                    const parsed = new Date(normalized);
                    if (!isNaN(parsed.getTime())) return formatFullDateTime(parsed);
                }
                return normalized.length > 120 ? `${normalized.slice(0, 120)}…` : normalized;
            }
            if (Array.isArray(currentValue)) {
                if (currentValue.length === 0) return t("empty");
                const allPrimitive = currentValue.every(v => v === null || typeof v !== "object");
                if (allPrimitive) return currentValue.map(format).join(", ");
                return t("{{count}} items", {count: currentValue.length});
            }
            if (typeof currentValue === "object") {
                const labelKeys = ["name", "title", "label", "code", "firstName", "email", "status", "type"];
                const found = labelKeys.find(key =>
                    currentValue[key] !== undefined && currentValue[key] !== null && currentValue[key] !== ""
                );
                if (found) return String(currentValue[found]);
                const keys = Object.keys(currentValue);
                return keys.length ? t("{{count}} fields", {count: keys.length}) : t("empty");
            }
            return String(currentValue);
        };

        return format(value);
    }, [t]);

    const buildChanges = React.useCallback((item: UserLogs): LogChange[] => {
        if (Array.isArray(item.changes)) {
            return item.changes.map(change => {
                const isResource = isResourceKey(change.path);
                const format = isResource ? formatResourceValue : formatValue;

                return {
                    label: isResource
                        ? humanizeKey(change.path).replace(/\s*\bids\b\s*$/i, "s").replace(/\s*\bid\b\s*$/i, "")
                        : humanizeKey(change.path),
                    oldValue: change.redacted ? t("Redacted") : format(change.before),
                    newValue: change.redacted ? t("Redacted") : format(change.after),
                    changed: true,
                    redacted: change.redacted
                };
            });
        }

        const oldObj = safeParse(item.oldValueJson);
        const newObj = safeParse(item.newValueJson);

        const ignored = new Set(["updateDate", "insertDate", "id", "_id", "_class"]);
        const keys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]))
            .filter(k => !ignored.has(k));

        return keys.map(key => {
            const isResource = isResourceKey(key);
            const format = isResource ? formatResourceValue : formatValue;
            const oldValue = format(oldObj[key]);
            const newValue = format(newObj[key]);
            return {
                // Resource id fields display resource names, so drop the "id"/"ids"
                // suffix from the label (e.g. "Resource ids" -> "Resources").
                label: isResource
                    ? humanizeKey(key).replace(/\s*\bids\b\s*$/i, "s").replace(/\s*\bid\b\s*$/i, "")
                    : humanizeKey(key),
                oldValue,
                newValue,
                changed: oldValue !== newValue
            };
        });
    }, [formatResourceValue, formatValue, t]);

    const formatUserFullName = React.useCallback((fullName: string | null | undefined): string => {
        const sanitized = fullName
            ?.split(/\s+/)
            .filter(part => {
                const normalized = part.trim().toLowerCase();
                return !!normalized && normalized !== "null" && normalized !== "undefined";
            })
            .join(" ")
            .trim();

        return sanitized || t("System");
    }, [t]);

    const actionLabel = React.useCallback((action: string | null | undefined): string => {
        switch (action) {
            case ActionType.CREATE:
                return t("Created");
            case ActionType.UPDATE:
                return t("Updated");
            case ActionType.DELETE:
                return t("Deleted");
            case AuditAction.IMPERSONATE:
                return t("Support");
            default:
                return action ? humanizeKey(action) : "-";
        }
    }, [t]);

    const filterOptions = useMemo<FilterOption[]>(() => [
        {
            field: "action",
            name: t("Action"),
            localizations: {},
            description: "",
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: AUDIT_ACTIONS.map(action => ({
                cid: action,
                name: actionLabel(action),
                colorCode: ""
            })),
            enumValues: AUDIT_ACTIONS,
            dataType: DataType.ENUM
        },
        {
            field: "userId",
            name: t("User"),
            localizations: {},
            description: "",
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: "user"
        },
        {
            field: "sectorId",
            name: t("Sector"),
            localizations: {},
            description: "",
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: "sector"
        },
        {
            field: "occurredAt",
            name: t("Timestamp"),
            localizations: {},
            description: "",
            allowedOperators: [
                DataTypeOperator.BETWEEN,
                DataTypeOperator.MORE_THAN_EQUAL,
                DataTypeOperator.LESS_THAN_EQUAL
            ],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        },
        {
            field: "targetId",
            name: t("Target ID"),
            localizations: {},
            description: "",
            allowedOperators: [DataTypeOperator.EQUAL],
            predefinedValues: [],
            dataType: DataType.TEXT
        },
        {
            field: "source",
            name: t("Source"),
            localizations: {},
            description: "",
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: AUDIT_SOURCES.map(source => ({
                cid: source,
                name: t(enumLabel(source)),
                colorCode: ""
            })),
            enumValues: AUDIT_SOURCES,
            dataType: DataType.ENUM
        },
        {
            field: "outcome",
            name: t("Outcome"),
            localizations: {},
            description: "",
            allowedOperators: [DataTypeOperator.IN],
            predefinedValues: AUDIT_OUTCOMES.map(outcome => ({
                cid: outcome,
                name: t(enumLabel(outcome)),
                colorCode: ""
            })),
            enumValues: AUDIT_OUTCOMES,
            dataType: DataType.ENUM
        }
    ], [actionLabel, t]);

    const actionColor = React.useCallback((action: string | null | undefined): string => {
        switch (action) {
            case AuditAction.CREATE:
                return theme.palette.success.main;
            case AuditAction.UPDATE:
            case AuditAction.ACCESS_CHANGE:
            case AuditAction.SEND:
            case AuditAction.CONFIGURE:
            case AuditAction.EXECUTE:
                return theme.palette.info.main;
            case AuditAction.DELETE:
                return theme.palette.error.main;
            case AuditAction.EXPORT:
            case AuditAction.IMPORT:
            case AuditAction.IMPERSONATE:
            case AuditAction.REFUND:
                return theme.palette.warning.main;
            default:
                return theme.palette.text.secondary;
        }
    }, [theme]);

    const renderAction = React.useCallback((item: UserLogs) => {
        const action = effectiveAction(item);
        const color = actionColor(action);

        return (
            <ElementChip
                text={actionLabel(action)}
                startIcon={(
                    <Box
                        component="span"
                        sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: color, flex: "0 0 auto"}}
                    />
                )}
                borderColor={alpha(color, 0.34)}
                backgroundColor={alpha(color, 0.06)}
            />
        );
    }, [actionColor, actionLabel]);

    const renderOutcome = React.useCallback((value: AuditOutcome | null | undefined) => {
        const effectiveOutcome = value || AuditOutcome.SUCCESS;
        const color = effectiveOutcome === AuditOutcome.FAILURE
            ? theme.palette.error.main
            : effectiveOutcome === AuditOutcome.PARTIAL
                ? theme.palette.warning.main
                : theme.palette.success.main;

        return (
            <ElementChip
                text={enumLabel(effectiveOutcome)}
                startIcon={(
                    <Box
                        component="span"
                        sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: color, flex: "0 0 auto"}}
                    />
                )}
                borderColor={alpha(color, 0.34)}
                backgroundColor={alpha(color, 0.06)}
            />
        );
    }, [theme]);

    const renderChanges = React.useCallback((item: UserLogs) => {
        const changes = buildChanges(item).filter(c => c.changed);
        const action = effectiveAction(item);

        if (changes.length === 0) {
            // No field-level diff stored (e.g. create / delete actions).
            if (action === ActionType.CREATE) {
                return <Typography variant="body2">{t("Entry was created")}</Typography>;
            }
            if (action === ActionType.DELETE) {
                return <Typography variant="body2">{t("Entry was deleted")}</Typography>;
            }
            return <Typography variant="body2" color="text.secondary">{t("No detailed changes recorded")}</Typography>;
        }

        const isCreate = action === ActionType.CREATE;
        const isDelete = action === ActionType.DELETE;

        return (
            <Stack sx={{gap: 0.75, py: 0.5}}>
                {changes.map((change, i) => (
                    <Stack key={i} direction="row" sx={{flexWrap: "wrap", alignItems: "center", gap: 0.75}}>
                        <Typography variant="body2" sx={{fontWeight: 600}}>
                            {change.label}:
                        </Typography>

                        {change.redacted ? (
                            <Typography variant="body2" color="text.secondary">
                                {t("Value changed (redacted)")}
                            </Typography>
                        ) : !isCreate && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "error.main",
                                    textDecoration: isDelete ? "none" : "line-through",
                                    opacity: 0.85
                                }}
                            >
                                {change.oldValue}
                            </Typography>
                        )}

                        {!change.redacted && !isCreate && !isDelete && (
                            <ArrowRightAltIcon fontSize="small" sx={{color: "text.disabled"}}/>
                        )}

                        {!change.redacted && !isDelete && (
                            <Typography variant="body2" sx={{color: "success.main", fontWeight: 500}}>
                                {change.newValue}
                            </Typography>
                        )}
                    </Stack>
                ))}
            </Stack>
        );
    }, [buildChanges, t]);

    const columns = useMemo<ColumnDef<UserLogs>[]>(() => [
        {
            id: "insertDate",
            header: t("Timestamp"),
            accessorFn: log => log.occurredAt || log.insertDate,
            cell: ({row}) => formatFullDateTime(logTimestamp(row.original)),
            size: 200,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: "insertDate"}
        },
        {
            id: "eventType",
            header: t("Event"),
            accessorFn: log => log.eventType,
            cell: ({row}) => row.original.eventType ? humanizeKey(row.original.eventType) : "-",
            size: 190,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: "eventType"}
        },
        {
            id: "actionType",
            header: t("Action"),
            accessorFn: log => effectiveAction(log),
            cell: ({row}) => renderAction(row.original),
            size: 120,
            meta: {defaultActive: true}
        },
        {
            id: "target",
            header: t("Target"),
            accessorFn: log => log.target?.displayName || log.target?.id || log.entityId,
            cell: ({row}) => targetLabel(row.original),
            size: 190,
            meta: {defaultActive: true}
        },
        {
            id: "targetId",
            header: t("Target ID"),
            accessorFn: log => log.target?.id || log.entityId,
            cell: ({row}) => row.original.target?.id || row.original.entityId || "-",
            size: 240,
            meta: {defaultActive: false}
        },
        {
            id: "sectorId",
            header: t("Sector"),
            accessorFn: log => log.sector?.id || log.sectorId,
            cell: ({row}) => row.original.sector?.displayName
                || sectorName(row.original.sector?.id || row.original.sectorId),
            size: 160,
            meta: {defaultActive: true}
        },
        {
            id: "userFullName",
            header: t("User"),
            accessorFn: log => log.actor?.displayName || log.userFullName,
            cell: ({row}) => formatUserFullName(row.original.actor?.displayName || row.original.userFullName),
            size: 160,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: "userFullName"}
        },
        {
            id: "source",
            header: t("Source"),
            accessorFn: log => log.source,
            cell: ({row}) => enumLabel(row.original.source),
            size: 150,
            meta: {defaultActive: false}
        },
        {
            id: "outcome",
            header: t("Outcome"),
            accessorFn: log => log.outcome || AuditOutcome.SUCCESS,
            cell: ({row}) => renderOutcome(row.original.outcome),
            size: 125,
            meta: {defaultActive: false}
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: log => log.id,
            cell: ({row}) => row.original.id || "",
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: "changes",
            header: t("Changes"),
            cell: ({row}) => <Box>{renderChanges(row.original)}</Box>,
            size: 420,
            meta: {defaultActive: true, grow: true}
        }
    ], [formatUserFullName, renderAction, renderChanges, renderOutcome, sectorName, t]);

    const controller = useServerTable<UserLogs>({
        tableId: "system-logs",
        columns,
        getRowId: log => log.id ?? "",
        initialSorting: [{id: "insertDate", desc: true}],
        queryFn: ({pagination, sorting, filterRules}) => {
            const {startDate, endDate} = auditDateRange(filterRules);
            const targetId = firstTextFilterValue(filterRules, "targetId");

            return LogsService.getUserLogs({
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : [{field: "insertDate", direction: "DESC"}]
                },
                userIds: textFilterValues(filterRules, "userId"),
                sectorIds: textFilterValues(filterRules, "sectorId"),
                actionTypes: enumFilterValues(filterRules, "action", AUDIT_ACTIONS),
                sources: enumFilterValues(filterRules, "source", AUDIT_SOURCES),
                outcomes: enumFilterValues(filterRules, "outcome", AUDIT_OUTCOMES),
                ...(targetId ? {entityId: targetId} : {}),
                ...(startDate ? {startDate} : {}),
                ...(endDate ? {endDate} : {})
            }).then(page => ({content: page.content ?? [], totalElements: page.totalElements ?? 0}));
        }
    });

    return (
        <Box sx={{display: "flex", flexDirection: "column", flex: 1, width: "100%", minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSort/>
                        <ServerTableFilter options={filterOptions}/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );
};

export default LogsTable;
